import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Sparkles,
  Upload,
  Loader2,
  Image as ImageIcon,
  Check,
  X,
  Wand2,
  SunMedium,
  Contrast,
  Palette,
  RotateCcw,
  FlipHorizontal,
  FlipVertical,
  Trash2,
  Download,
  Plus,
  Crop,
  Maximize,
  Filter,
  Brain,
  Target
} from 'lucide-react';
import { api, isUpgradeError } from '../context/AuthContext';
import { CreditCost } from './CreditMeter';

export const ImageEditor = ({ open, onClose, onImageSelect }) => {
  const [activeTab, setActiveTab] = useState('generate');
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const fileInputRef = useRef(null);

  // Generation form state
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('1024x1024');
  const [style, setStyle] = useState('vivid');

  // Edit state
  const [editSettings, setEditSettings] = useState({
    brightness: 1.0,
    contrast: 1.0,
    saturation: 1.0,
    blur: 0,
    rotate: 0,
    flip_horizontal: false,
    flip_vertical: false
  });

  // Crop state
  const [cropPreset, setCropPreset] = useState('instagram_square');
  const [focusX, setFocusX] = useState(0.5);
  const [focusY, setFocusY] = useState(0.5);

  // Aspect ratio presets
  const ASPECT_PRESETS = {
    instagram_square: { name: 'Instagram Square', ratio: '1:1', icon: '📷' },
    instagram_portrait: { name: 'Instagram Portrait', ratio: '4:5', icon: '📷' },
    instagram_story: { name: 'Instagram Story', ratio: '9:16', icon: '📷' },
    instagram_landscape: { name: 'Instagram Landscape', ratio: '1.91:1', icon: '📷' },
    facebook_post: { name: 'Facebook Post', ratio: '1.91:1', icon: '👤' },
    facebook_cover: { name: 'Facebook Cover', ratio: '2.63:1', icon: '👤' },
    facebook_story: { name: 'Facebook Story', ratio: '9:16', icon: '👤' },
    twitter_post: { name: 'Twitter/X Post', ratio: '16:9', icon: '🐦' },
    twitter_header: { name: 'Twitter/X Header', ratio: '3:1', icon: '🐦' },
    linkedin_post: { name: 'LinkedIn Post', ratio: '1.91:1', icon: '💼' },
    linkedin_cover: { name: 'LinkedIn Cover', ratio: '4:1', icon: '💼' },
    pinterest: { name: 'Pinterest Pin', ratio: '2:3', icon: '📌' },
    youtube_thumbnail: { name: 'YouTube Thumbnail', ratio: '16:9', icon: '▶️' },
    tiktok: { name: 'TikTok', ratio: '9:16', icon: '🎵' },
  };

  // Load images on open
  useEffect(() => {
    if (open) {
      loadImages();
    }
  }, [open]);

  // Poll for generation status
  useEffect(() => {
    let interval;
    if (generationStatus?.status === 'pending' || generationStatus?.status === 'processing') {
      interval = setInterval(async () => {
        try {
          const response = await api.get(`/images/status/${generationStatus.image_id}`);
          setGenerationStatus(response.data);
          
          if (response.data.status === 'completed') {
            toast.success('Image generated successfully!');
            loadImages();
            setSelectedImage(response.data);
          } else if (response.data.status === 'failed') {
            toast.error(`Generation failed: ${response.data.error}`);
          }
        } catch (error) {
          console.error('Status check error:', error);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [generationStatus?.image_id, generationStatus?.status]);

  const loadImages = async () => {
    try {
      const response = await api.get('/images/list');
      setImages(response.data || []);
    } catch (error) {
      console.error('Failed to load images:', error);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/images/generate', {
        prompt: prompt.trim(),
        model: 'gpt-image-1',
        size,
        style
      });
      setGenerationStatus(response.data);
      toast.success('Image generation started!');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error(error.response?.data?.detail || 'Failed to start generation');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const response = await api.post('/images/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded!');
      loadImages();
      setSelectedImage(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyEdits = async () => {
    if (!selectedImage) return;

    setLoading(true);
    try {
      const response = await api.post('/images/edit', {
        image_id: selectedImage.image_id,
        ...editSettings
      });
      toast.success('Edits applied!');
      loadImages();
      setSelectedImage(response.data);
      // Reset edit settings
      setEditSettings({
        brightness: 1.0,
        contrast: 1.0,
        saturation: 1.0,
        blur: 0,
        rotate: 0,
        flip_horizontal: false,
        flip_vertical: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Edit failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = async (filterType) => {
    if (!selectedImage) return;

    setLoading(true);
    try {
      const response = await api.post('/images/filter', {
        image_id: selectedImage.image_id,
        filter_type: filterType
      });
      toast.success(`${filterType} filter applied!`);
      loadImages();
      setSelectedImage(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Filter failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCropAspect = async () => {
    if (!selectedImage) return;

    setLoading(true);
    try {
      const response = await api.post('/images/crop-aspect', {
        image_id: selectedImage.image_id,
        preset: cropPreset,
        focus_x: focusX,
        focus_y: focusY
      });
      toast.success(`Cropped to ${ASPECT_PRESETS[cropPreset]?.name || cropPreset}!`);
      loadImages();
      setSelectedImage(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Crop failed');
    } finally {
      setLoading(false);
    }
  };

  const [smartFocusLoading, setSmartFocusLoading] = useState(false);
  const [smartFocusResult, setSmartFocusResult] = useState(null);

  const handleSmartFocus = async () => {
    if (!selectedImage) return;

    setSmartFocusLoading(true);
    setSmartFocusResult(null);
    try {
      const response = await api.post('/images/smart-focus', {
        image_id: selectedImage.image_id
      });
      
      // Update focus point sliders
      setFocusX(response.data.focus_x);
      setFocusY(response.data.focus_y);
      setSmartFocusResult(response.data);
      
      toast.success(`AI detected: ${response.data.subject_description}`);
    } catch (error) {
      if (!isUpgradeError(error)) toast.error(error.response?.data?.detail || 'Smart focus analysis failed');
    } finally {
      setSmartFocusLoading(false);
    }
  };

  const handleSelectForPost = () => {
    const imageUrl = getImageUrl(selectedImage);
    if (imageUrl) {
      onImageSelect?.(imageUrl);
      onClose();
      toast.success('Image added to post!');
    } else {
      toast.error('No image selected');
    }
  };

  const handleDelete = async (imageId) => {
    try {
      await api.delete(`/images/${imageId}`);
      toast.success('Image deleted');
      loadImages();
      if (selectedImage?.image_id === imageId) {
        setSelectedImage(null);
      }
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    const relativeUrl = image.output_url || `/api/images/view/${image.image_id}`;
    if (relativeUrl.startsWith('http')) return relativeUrl;
    return `${process.env.REACT_APP_BACKEND_URL}${relativeUrl}`;
  };

  const hasEditChanges = () => {
    return editSettings.brightness !== 1.0 ||
           editSettings.contrast !== 1.0 ||
           editSettings.saturation !== 1.0 ||
           editSettings.blur !== 0 ||
           editSettings.rotate !== 0 ||
           editSettings.flip_horizontal ||
           editSettings.flip_vertical;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="image-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-blue-600" />
            Image Studio
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="generate" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Generate
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="crop" className="flex items-center gap-2">
              <Crop className="h-4 w-4" />
              Crop
            </TabsTrigger>
            <TabsTrigger value="edit" className="flex items-center gap-2">
              <Wand2 className="h-4 w-4" />
              Edit
            </TabsTrigger>
          </TabsList>

          {/* GENERATE TAB */}
          <TabsContent value="generate" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <Label>Describe your image</Label>
                  <Textarea
                    placeholder="A serene sunset over mountains with vibrant orange and purple sky..."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="mt-1 min-h-[120px]"
                    data-testid="image-prompt-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Size</Label>
                    <Select value={size} onValueChange={setSize}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1024x1024">Square (1024x1024)</SelectItem>
                        <SelectItem value="1792x1024">Landscape (1792x1024)</SelectItem>
                        <SelectItem value="1024x1792">Portrait (1024x1792)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Style</Label>
                    <Select value={style} onValueChange={setStyle}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vivid">Vivid</SelectItem>
                        <SelectItem value="natural">Natural</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={handleGenerate} 
                  disabled={loading || !prompt.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="generate-image-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Image
                      <CreditCost feature="image_generation" />
                    </>
                  )}
                </Button>

                {generationStatus && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Generation Status</span>
                      <Badge variant={
                        generationStatus.status === 'completed' ? 'default' :
                        generationStatus.status === 'failed' ? 'destructive' : 'secondary'
                      }>
                        {generationStatus.status}
                      </Badge>
                    </div>
                    {(generationStatus.status === 'pending' || generationStatus.status === 'processing') && (
                      <div className="space-y-2">
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${generationStatus.progress || 10}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-500">This may take 30-60 seconds</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border rounded-lg p-4 bg-slate-50 min-h-[300px] flex items-center justify-center">
                {generationStatus?.status === 'completed' && generationStatus?.output_url ? (
                  <img
                    src={getImageUrl(generationStatus)}
                    alt="Generated"
                    className="max-w-full max-h-[280px] rounded-lg shadow-lg"
                  />
                ) : (
                  <div className="text-center text-slate-400">
                    <ImageIcon className="h-16 w-16 mx-auto mb-2 opacity-50" />
                    <p>Generated image will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* UPLOAD TAB */}
          <TabsContent value="upload" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                >
                  <Upload className="h-12 w-12 mx-auto mb-2 text-slate-400" />
                  <p className="text-slate-600 font-medium">Click to upload</p>
                  <p className="text-sm text-slate-400">PNG, JPG, GIF, WebP</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium">Image Library</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2 max-h-[250px] overflow-y-auto">
                    {images.filter(img => img.status === 'completed').map((image) => (
                      <div
                        key={image.image_id}
                        onClick={() => setSelectedImage(image)}
                        className={`group relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          selectedImage?.image_id === image.image_id 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : 'border-transparent hover:border-blue-300'
                        }`}
                      >
                        <img
                          src={getImageUrl(image)}
                          alt={image.name || image.prompt || 'Image'}
                          className="w-full aspect-square object-cover"
                        />
                        <Badge className="absolute top-1 left-1 text-[10px] px-1 py-0">
                          {image.type}
                        </Badge>
                        {selectedImage?.image_id === image.image_id && (
                          <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                            <Check className="h-6 w-6 text-blue-600 bg-white rounded-full p-1" />
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(image.image_id);
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {images.filter(img => img.status === 'completed').length === 0 && (
                      <p className="col-span-3 text-center text-slate-400 py-4">No images yet</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-slate-50 min-h-[300px] flex items-center justify-center">
                {selectedImage ? (
                  <div className="text-center">
                    <img
                      src={getImageUrl(selectedImage)}
                      alt="Selected"
                      className="max-w-full max-h-[260px] rounded-lg shadow-lg mx-auto"
                    />
                    <p className="mt-2 text-sm text-slate-600 truncate max-w-[300px] mx-auto">
                      {selectedImage.name || selectedImage.prompt || selectedImage.image_id}
                    </p>
                  </div>
                ) : (
                  <div className="text-center text-slate-400">
                    <ImageIcon className="h-16 w-16 mx-auto mb-2 opacity-50" />
                    <p>Select an image from your library</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* CROP TAB */}
          <TabsContent value="crop" className="space-y-4">
            {selectedImage ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  {/* Platform Presets */}
                  <Card>
                    <CardContent className="pt-4 space-y-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <Crop className="h-4 w-4" />
                        Platform Presets
                      </h4>
                      
                      {/* Instagram */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Instagram</p>
                        <div className="grid grid-cols-2 gap-2">
                          {['instagram_square', 'instagram_portrait', 'instagram_story', 'instagram_landscape'].map(preset => (
                            <Button
                              key={preset}
                              variant={cropPreset === preset ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCropPreset(preset)}
                              className="text-xs justify-start"
                            >
                              <span className="mr-1">{ASPECT_PRESETS[preset].icon}</span>
                              {ASPECT_PRESETS[preset].name.replace('Instagram ', '')}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Facebook */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Facebook</p>
                        <div className="grid grid-cols-2 gap-2">
                          {['facebook_post', 'facebook_cover', 'facebook_story'].map(preset => (
                            <Button
                              key={preset}
                              variant={cropPreset === preset ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCropPreset(preset)}
                              className="text-xs justify-start"
                            >
                              <span className="mr-1">{ASPECT_PRESETS[preset].icon}</span>
                              {ASPECT_PRESETS[preset].name.replace('Facebook ', '')}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Twitter & LinkedIn */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Twitter/X & LinkedIn</p>
                        <div className="grid grid-cols-2 gap-2">
                          {['twitter_post', 'twitter_header', 'linkedin_post', 'linkedin_cover'].map(preset => (
                            <Button
                              key={preset}
                              variant={cropPreset === preset ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCropPreset(preset)}
                              className="text-xs justify-start"
                            >
                              <span className="mr-1">{ASPECT_PRESETS[preset].icon}</span>
                              {ASPECT_PRESETS[preset].name.replace('Twitter/X ', '').replace('LinkedIn ', '')}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Other Platforms */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Other</p>
                        <div className="grid grid-cols-2 gap-2">
                          {['pinterest', 'youtube_thumbnail', 'tiktok'].map(preset => (
                            <Button
                              key={preset}
                              variant={cropPreset === preset ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCropPreset(preset)}
                              className="text-xs justify-start"
                            >
                              <span className="mr-1">{ASPECT_PRESETS[preset].icon}</span>
                              {ASPECT_PRESETS[preset].name}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Focus Point */}
                  <Card>
                    <CardContent className="pt-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium flex items-center gap-2">
                          <Maximize className="h-4 w-4" />
                          Focus Point
                        </h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSmartFocus}
                          disabled={smartFocusLoading}
                          className="gap-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                          data-testid="smart-focus-btn"
                        >
                          {smartFocusLoading ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Brain className="h-3 w-3" />
                          )}
                          AI Detect
                          <CreditCost feature="image_analysis" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">Adjust where the crop centers on your image</p>
                      
                      {smartFocusResult && (
                        <div className="p-2 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-start gap-2">
                            <Target className="h-4 w-4 text-purple-600 mt-0.5" />
                            <div className="text-xs">
                              <p className="font-medium text-purple-800">{smartFocusResult.subject_description}</p>
                              <p className="text-purple-600">Confidence: {smartFocusResult.confidence}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Horizontal</span>
                            <span>{Math.round(focusX * 100)}%</span>
                          </div>
                          <Slider
                            value={[focusX]}
                            onValueChange={([v]) => {setFocusX(v); setSmartFocusResult(null);}}
                            min={0}
                            max={1}
                            step={0.01}
                          />
                          <div className="flex justify-between text-xs text-slate-400 mt-1">
                            <span>Left</span>
                            <span>Center</span>
                            <span>Right</span>
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Vertical</span>
                            <span>{Math.round(focusY * 100)}%</span>
                          </div>
                          <Slider
                            value={[focusY]}
                            onValueChange={([v]) => {setFocusY(v); setSmartFocusResult(null);}}
                            min={0}
                            max={1}
                            step={0.01}
                          />
                          <div className="flex justify-between text-xs text-slate-400 mt-1">
                            <span>Top</span>
                            <span>Center</span>
                            <span>Bottom</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Button 
                    onClick={handleCropAspect} 
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="crop-image-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Crop className="h-4 w-4 mr-2" />
                    )}
                    Crop to {ASPECT_PRESETS[cropPreset]?.name || cropPreset}
                  </Button>
                </div>

                {/* Preview with crop overlay */}
                <div className="border rounded-lg p-4 bg-slate-900 min-h-[400px] flex items-center justify-center">
                  <div className="text-center relative">
                    <div className="relative inline-block">
                      <img
                        src={getImageUrl(selectedImage)}
                        alt="Crop preview"
                        className="max-w-full max-h-[360px] rounded-lg opacity-50"
                      />
                      {/* Crop overlay indicator */}
                      <div 
                        className="absolute border-2 border-blue-500 rounded pointer-events-none"
                        style={{
                          left: `${focusX * 30}%`,
                          top: `${focusY * 30}%`,
                          width: '70%',
                          height: '70%',
                          boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)'
                        }}
                      />
                    </div>
                    <div className="mt-3 text-white">
                      <Badge className="bg-blue-600">{ASPECT_PRESETS[cropPreset]?.ratio}</Badge>
                      <p className="text-xs text-slate-300 mt-1">{ASPECT_PRESETS[cropPreset]?.name}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Crop className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Select an image from your library to crop</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setActiveTab('upload')}
                >
                  Go to Library
                </Button>
              </div>
            )}
          </TabsContent>

          {/* EDIT TAB */}
          <TabsContent value="edit" className="space-y-4">
            {selectedImage ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  {/* Adjustments */}
                  <Card>
                    <CardContent className="pt-4 space-y-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <SunMedium className="h-4 w-4" />
                        Adjustments
                      </h4>

                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Brightness</span>
                            <span>{editSettings.brightness.toFixed(1)}</span>
                          </div>
                          <Slider
                            value={[editSettings.brightness]}
                            onValueChange={([v]) => setEditSettings(s => ({ ...s, brightness: v }))}
                            min={0.5}
                            max={2.0}
                            step={0.1}
                          />
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Contrast</span>
                            <span>{editSettings.contrast.toFixed(1)}</span>
                          </div>
                          <Slider
                            value={[editSettings.contrast]}
                            onValueChange={([v]) => setEditSettings(s => ({ ...s, contrast: v }))}
                            min={0.5}
                            max={2.0}
                            step={0.1}
                          />
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Saturation</span>
                            <span>{editSettings.saturation.toFixed(1)}</span>
                          </div>
                          <Slider
                            value={[editSettings.saturation]}
                            onValueChange={([v]) => setEditSettings(s => ({ ...s, saturation: v }))}
                            min={0}
                            max={2.0}
                            step={0.1}
                          />
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Blur</span>
                            <span>{editSettings.blur}</span>
                          </div>
                          <Slider
                            value={[editSettings.blur]}
                            onValueChange={([v]) => setEditSettings(s => ({ ...s, blur: v }))}
                            min={0}
                            max={10}
                            step={1}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Transform */}
                  <Card>
                    <CardContent className="pt-4 space-y-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <RotateCcw className="h-4 w-4" />
                        Transform
                      </h4>

                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Rotate</span>
                          <span>{editSettings.rotate}°</span>
                        </div>
                        <Slider
                          value={[editSettings.rotate]}
                          onValueChange={([v]) => setEditSettings(s => ({ ...s, rotate: v }))}
                          min={-180}
                          max={180}
                          step={1}
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant={editSettings.flip_horizontal ? "default" : "outline"}
                          size="sm"
                          onClick={() => setEditSettings(s => ({ ...s, flip_horizontal: !s.flip_horizontal }))}
                          className="flex-1"
                        >
                          <FlipHorizontal className="h-4 w-4 mr-1" />
                          Flip H
                        </Button>
                        <Button
                          variant={editSettings.flip_vertical ? "default" : "outline"}
                          size="sm"
                          onClick={() => setEditSettings(s => ({ ...s, flip_vertical: !s.flip_vertical }))}
                          className="flex-1"
                        >
                          <FlipVertical className="h-4 w-4 mr-1" />
                          Flip V
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Filters */}
                  <Card>
                    <CardContent className="pt-4 space-y-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        Filters
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        {['grayscale', 'sepia', 'vintage', 'blur', 'sharpen', 'edge'].map(filter => (
                          <Button
                            key={filter}
                            variant="outline"
                            size="sm"
                            onClick={() => handleApplyFilter(filter)}
                            disabled={loading}
                            className="capitalize"
                          >
                            {filter}
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {hasEditChanges() && (
                    <Button 
                      onClick={handleApplyEdits} 
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Apply Edits
                    </Button>
                  )}
                </div>

                <div className="border rounded-lg p-4 bg-slate-50 min-h-[400px] flex items-center justify-center">
                  <div className="text-center">
                    <img
                      src={getImageUrl(selectedImage)}
                      alt="Editing"
                      className="max-w-full max-h-[360px] rounded-lg shadow-lg mx-auto"
                      style={{
                        filter: `brightness(${editSettings.brightness}) contrast(${editSettings.contrast}) saturate(${editSettings.saturation}) blur(${editSettings.blur}px)`,
                        transform: `rotate(${editSettings.rotate}deg) scaleX(${editSettings.flip_horizontal ? -1 : 1}) scaleY(${editSettings.flip_vertical ? -1 : 1})`
                      }}
                    />
                    <p className="mt-2 text-xs text-slate-500">Preview (adjustments shown client-side)</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <ImageIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Select an image from your library to edit</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setActiveTab('upload')}
                >
                  Go to Library
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer Actions */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSelectForPost}
            disabled={!selectedImage}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="use-image-in-post-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Use in Post
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageEditor;
